/**
 * DevPageHeader — Consistent header for DEVELOPMENT section pages.
 *
 * Sprint picker has moved to the sidebar; these headers show only
 * the page identity (icon + title + subtitle) and optional actions.
 */

import type { ComponentType, ReactNode } from 'react';
import React from 'react';

export interface DevPageHeaderProps {
  icon: ComponentType<{ size?: number; className?: string }>;
  title: string;
  subtitle?: React.ReactNode;
  actions?: ReactNode;
  statusBadge?: ReactNode;
}

export function DevPageHeader({ icon: Icon, title, subtitle, actions, statusBadge }: DevPageHeaderProps) {
  return (
    <div className="bg-white border-b border-line px-6 py-3 relative">
      <div className="flex items-center justify-between max-w-[1400px] mx-auto">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-coral-500 to-coral-600 flex items-center justify-center shadow-sm">
            <Icon size={16} className="text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-[15px] font-bold text-text">{title}</h1>
              {statusBadge}
            </div>
            {subtitle && (
              <div className="text-[11px] text-subtle">{subtitle}</div>
            )}
          </div>
        </div>
        {actions && (
          <div className="flex items-center gap-2">
            {actions}
          </div>
        )}
      </div>
      {/* Subtle accent line at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-coral-400/60 via-coral-300/20 to-transparent" />
    </div>
  );
}
